import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Checkbox,
  Button,
  Typography,
  Popover,
  InputAdornment,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

/**
 * Generic popover for filtering by employee role.
 * Works similarly to StatusFilterPopover but options are provided via props.
 */
const RoleFilterPopover = ({ options = [], activeFilters = [], onFilterChange }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoles, setSelectedRoles] = useState({});

  const open = Boolean(anchorEl);
  const isFilterActive = activeFilters.length > 0;

  // Initialize selectedRoles from activeFilters
  useEffect(() => {
    if (activeFilters.length > 0) {
      const initialSelected = {};
      activeFilters.forEach((role) => {
        initialSelected[role] = true;
      });
      setSelectedRoles(initialSelected);
    } else {
      setSelectedRoles({});
    }
  }, [activeFilters]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles((prev) => {
      const next = { ...prev };
      if (next[roleId]) {
        delete next[roleId];
      } else {
        next[roleId] = true;
      }
      return next;
    });
  };

  const handleApplyFilter = () => {
    const selectedValues = Object.keys(selectedRoles);
    onFilterChange(selectedValues);
    handleFilterClose();
  };

  const handleResetFilter = () => {
    setSelectedRoles({});
    onFilterChange([]);
    handleFilterClose();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const normalizedOptions = Array.from(new Set(options || [])).filter(Boolean);
  const filteredOptions = normalizedOptions.filter((role) =>
    role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatRoleLabel = (role) => {
    const r = String(role || "").trim();
    if (!r) return "";
    return r.charAt(0).toUpperCase() + r.slice(1);
  };

  const styles = {
    popover: {
      width: "180px",
      maxHeight: "300px",
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[4],
      border: `1px solid ${theme.palette.divider}`,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(0.75)} ${theme.spacing(
        1.5
      )}`,
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    searchInput: {
      "& .MuiOutlinedInput-root": {
        height: "32px",
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(0.75),
        fontSize: "12px",
        "& fieldset": {
          borderColor: theme.palette.grey[300],
        },
        "&:hover fieldset": {
          borderColor: theme.palette.primary.main,
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: "1px",
        },
      },
    },
    searchIcon: {
      fontSize: 16,
      color: theme.palette.grey[500],
    },
    content: {
      maxHeight: "160px",
      overflowY: "auto",
      backgroundColor: theme.palette.background.paper,
      "&::-webkit-scrollbar": {
        width: "4px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.palette.grey[100],
      },
      "&::-webkit-scrollbar-thumb": {
        background: theme.palette.grey[300],
        borderRadius: "2px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: theme.palette.grey[400],
      },
    },
    optionRow: {
      display: "flex",
      alignItems: "center",
      padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: theme.palette.grey[50],
      },
    },
    optionText: {
      fontSize: "12px",
      color: theme.palette.text.secondary,
    },
    footer: {
      padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(
        1.5
      )}`,
      backgroundColor: theme.palette.grey[50],
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    buttonRow: {
      display: "flex",
      gap: theme.spacing(0.75),
    },
    resetButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 500,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
    },
    applyButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    noResults: {
      textAlign: "center",
      padding: theme.spacing(2),
      fontSize: "12px",
      color: theme.palette.grey[500],
    },
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleFilterClick}
        sx={{
          ml: 0.5,
          padding: "4px",
          backgroundColor: isFilterActive ? theme.palette.grey[200] : "transparent",
          borderRadius: theme.spacing(1),
          "&:hover": {
            backgroundColor: isFilterActive ? theme.palette.grey[300] : theme.palette.grey[100],
          },
        }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: styles.popover }}
      >
        <Box sx={styles.header}>
          <TextField
            size="small"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            variant="outlined"
            sx={styles.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={styles.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={styles.content}>
          {filteredOptions.map((role) => (
            <Box
              key={role}
              sx={styles.optionRow}
              onClick={() => handleRoleToggle(role)}
            >
              <Checkbox
                size="small"
                checked={Boolean(selectedRoles[role])}
                sx={{
                  mr: 1,
                  color: theme.palette.grey[600],
                  padding: theme.spacing(0.25),
                  "&.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                }}
              />
              <Typography sx={styles.optionText}>{formatRoleLabel(role)}</Typography>
            </Box>
          ))}

          {filteredOptions.length === 0 && searchQuery && (
            <Typography sx={styles.noResults}>No roles found</Typography>
          )}
        </Box>

        <Box sx={styles.footer}>
          <Box sx={styles.buttonRow}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetFilter}
              sx={styles.resetButton}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              sx={styles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default RoleFilterPopover;

